import axios from "axios";

// Multi-tenant resolution keys off the request Host, so every deployed page must
// call the API same-origin — an absolute base (e.g. https://crm.wizardsoftware.in)
// would send Host: crm… from zenith.wizardsoftware.in and resolve the wrong
// institution. REACT_APP_BACKEND_URL is honoured only when the page itself is
// running on localhost (the CRA dev server → separate API).
function resolveApiBase() {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocalDev = h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".local");
    if (!isLocalDev) return "/api";
  }
  const env = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return env ? `${env}/api` : "/api";
}

export const API = resolveApiBase();

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Tenant-suspension / Off-Season Hold gates (routes_auth.get_current_user,
// backend) send a plain-string 403 detail — "TENANT_SUSPENDED" the moment
// an institution's subscription lapses or is suspended, "TENANT_PAUSED"
// while a platform admin has it on seasonal hold — from ANY authenticated
// endpoint. This is the one global place that catches either regardless of
// which page/request triggered it, since it lives outside the React tree
// (no useNavigate() available here, hence a real navigation rather than
// client-side routing).
const GATE_REDIRECTS = {
  TENANT_SUSPENDED: "/tenant-suspended",
  TENANT_PAUSED: "/tenant-paused",
};
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const detail = error.response?.status === 403 ? error.response?.data?.detail : null;
    const target = detail && GATE_REDIRECTS[detail];
    if (target && typeof window !== "undefined" && window.location.pathname !== target) {
      window.location.href = target;
    }
    return Promise.reject(error);
  }
);

const IMP_KEY = "edupulse_impersonation";

// Restore an impersonation session across reloads.
try {
  const raw = sessionStorage.getItem(IMP_KEY);
  if (raw) api.defaults.headers.common.Authorization = `Bearer ${JSON.parse(raw).token}`;
} catch { /* ignore */ }

export function setImpersonation(payload) {
  // payload: { token, institution, acting_as }
  try { sessionStorage.setItem(IMP_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
  api.defaults.headers.common.Authorization = `Bearer ${payload.token}`;
}

export function clearImpersonation() {
  try { sessionStorage.removeItem(IMP_KEY); } catch { /* ignore */ }
  delete api.defaults.headers.common.Authorization;
}

export function getImpersonation() {
  try { return JSON.parse(sessionStorage.getItem(IMP_KEY) || "null"); } catch { return null; }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
